import { SaveManager } from '../../../state/SaveManager.js';
import type { DiscoveryLabDistrict } from './DiscoveryLabConfig.js';
import {
  DISCOVERY_LAB_DISTRICTS,
  DISCOVERY_LAB_MATERIALS_SLICE,
  DISCOVERY_LAB_POLLINATOR_SLICE,
} from './DiscoveryLabConfig.js';
import type { MaterialsInvestigationSnapshot } from './minigames/materials/MaterialsInvestigationState.js';
import type { RelationshipInvestigationSnapshot } from './minigames/relationship/RelationshipInvestigationState.js';

export const DISCOVERY_LAB_WORLD_ID = 'discovery-lab';

export type WashedOutTrailRestoration = 'damaged' | 'stage-1';
export type PollinatorGardenRestoration = 'needs-pollinators' | 'restored';
export type MaterialsWorkshopRestoration = 'needs-roof' | 'restored';

export interface DiscoveryLabEvidenceRecord {
  readonly evidenceIds: readonly string[];
  readonly conclusionId: string;
  readonly completedAt: number;
}

export interface DiscoveryLabMaterialTestRecord {
  readonly completedTestKeys: readonly string[];
  readonly evidenceIds: readonly string[];
  readonly selectedMaterialId?: string;
  readonly completedAt?: number;
}

export interface DiscoveryLabStateSnapshot {
  readonly visited: boolean;
  readonly currentDistrict: DiscoveryLabDistrict;
  readonly fieldCampusDiscovered: boolean;
  readonly washedOutTrailDiscovered: boolean;
  readonly investigationsCompleted: readonly string[];
  readonly investigationsStarted: readonly string[];
  readonly washedOutTrailRestoration: WashedOutTrailRestoration;
  readonly pollinatorGardenDiscovered: boolean;
  readonly pollinatorGardenRestoration: PollinatorGardenRestoration;
  readonly materialsWorkshopDiscovered: boolean;
  readonly materialsWorkshopRestoration: MaterialsWorkshopRestoration;
  readonly materialTestRecords: Readonly<Record<string, DiscoveryLabMaterialTestRecord>>;
  readonly evidenceRecords: Readonly<Record<string, DiscoveryLabEvidenceRecord>>;
  readonly unlockedDistricts: readonly DiscoveryLabDistrict[];
  readonly districtProgress: Readonly<Record<string, unknown>>;
  readonly completedDistricts: readonly DiscoveryLabDistrict[];
  readonly restorationEvents: readonly string[];
}

function isDiscoveryLabDistrict(value: string | undefined): value is DiscoveryLabDistrict {
  return Object.values(DISCOVERY_LAB_DISTRICTS).includes(value as DiscoveryLabDistrict);
}

function getProgress() {
  const world = SaveManager.getWorld(DISCOVERY_LAB_WORLD_ID);
  world.discoveryLab ??= {
    fieldCampusDiscovered: false,
    washedOutTrailDiscovered: false,
    investigationsCompleted: [],
    washedOutTrailRestoration: 'damaged',
    unlockedDistricts: [DISCOVERY_LAB_DISTRICTS.fieldCampus],
  };
  world.discoveryLab.washedOutTrailDiscovered ??=
    world.discoveryLab.investigationsCompleted.includes('washed-out-trail-1')
    || world.discoveryLab.washedOutTrailRestoration === 'stage-1';
  world.discoveryLab.investigationsStarted ??= [];
  world.discoveryLab.pollinatorGardenDiscovered ??=
    world.discoveryLab.investigationsCompleted.includes('pollinator-garden-1')
    || world.discoveryLab.pollinatorGardenRestoration === 'restored';
  world.discoveryLab.pollinatorGardenRestoration ??= 'needs-pollinators';
  world.discoveryLab.relationshipInvestigationSnapshots ??= {};
  world.discoveryLab.materialsWorkshopDiscovered ??=
    world.discoveryLab.investigationsCompleted.includes('materials-workshop-roof-1')
    || world.discoveryLab.materialsWorkshopRestoration === 'restored';
  world.discoveryLab.materialsWorkshopRestoration ??= 'needs-roof';
  world.discoveryLab.materialsInvestigationSnapshots ??= {};
  world.discoveryLab.materialTestRecords ??= {};
  world.discoveryLab.evidenceRecords ??= {};
  world.discoveryLab.districtProgress ??= {};
  world.discoveryLab.completedDistricts ??= [];
  world.discoveryLab.restorationEvents ??= [];
  if (
    world.discoveryLab.pollinatorGardenRestoration === 'restored'
    && !world.discoveryLab.unlockedDistricts.includes(DISCOVERY_LAB_DISTRICTS.pollinatorGarden)
  ) {
    world.discoveryLab.unlockedDistricts.push(DISCOVERY_LAB_DISTRICTS.pollinatorGarden);
  }
  if (
    world.discoveryLab.materialsWorkshopRestoration === 'restored'
    && !world.discoveryLab.unlockedDistricts.includes(DISCOVERY_LAB_DISTRICTS.materialsWorkshop)
  ) {
    world.discoveryLab.unlockedDistricts.push(DISCOVERY_LAB_DISTRICTS.materialsWorkshop);
  }
  if (
    world.discoveryLab.materialsWorkshopRestoration === 'restored'
    && !world.discoveryLab.unlockedDistricts.includes(DISCOVERY_LAB_DISTRICTS.weatherWatchStation)
  ) {
    world.discoveryLab.unlockedDistricts.push(DISCOVERY_LAB_DISTRICTS.weatherWatchStation);
  }
  return world.discoveryLab;
}

export function getDiscoveryLabState(): DiscoveryLabStateSnapshot {
  const world = SaveManager.getWorld(DISCOVERY_LAB_WORLD_ID);
  const progress = getProgress();
  const currentDistrict = isDiscoveryLabDistrict(world.currentDistrict)
    ? world.currentDistrict
    : DISCOVERY_LAB_DISTRICTS.fieldCampus;
  const unlockedDistricts = progress.unlockedDistricts.filter(isDiscoveryLabDistrict);

  if (!unlockedDistricts.includes(DISCOVERY_LAB_DISTRICTS.fieldCampus)) {
    unlockedDistricts.unshift(DISCOVERY_LAB_DISTRICTS.fieldCampus);
  }

  return {
    visited: world.visited === true,
    currentDistrict,
    fieldCampusDiscovered: progress.fieldCampusDiscovered,
    washedOutTrailDiscovered: progress.washedOutTrailDiscovered,
    investigationsCompleted: [...progress.investigationsCompleted],
    investigationsStarted: [...(progress.investigationsStarted ?? [])],
    washedOutTrailRestoration: progress.washedOutTrailRestoration,
    pollinatorGardenDiscovered: progress.pollinatorGardenDiscovered ?? false,
    pollinatorGardenRestoration: progress.pollinatorGardenRestoration ?? 'needs-pollinators',
    materialsWorkshopDiscovered: progress.materialsWorkshopDiscovered ?? false,
    materialsWorkshopRestoration: progress.materialsWorkshopRestoration ?? 'needs-roof',
    materialTestRecords: Object.fromEntries(
      Object.entries(progress.materialTestRecords ?? {}).map(([id, record]) => [id, {
        completedTestKeys: [...record.completedTestKeys],
        evidenceIds: [...record.evidenceIds],
        selectedMaterialId: record.selectedMaterialId,
        completedAt: record.completedAt,
      }]),
    ),
    evidenceRecords: Object.fromEntries(
      Object.entries(progress.evidenceRecords ?? {}).map(([id, record]) => [id, {
        evidenceIds: [...record.evidenceIds],
        conclusionId: record.conclusionId,
        completedAt: record.completedAt,
      }]),
    ),
    unlockedDistricts: [...unlockedDistricts],
    districtProgress: { ...(progress.districtProgress ?? {}) },
    completedDistricts: (progress.completedDistricts ?? []).filter(isDiscoveryLabDistrict),
    restorationEvents: [...(progress.restorationEvents ?? [])],
  };
}

export function isPollinatorGardenRouteAvailable() {
  const progress = getProgress();
  return DISCOVERY_LAB_POLLINATOR_SLICE.productionReady
    && progress.washedOutTrailRestoration === 'stage-1';
}

export function isMaterialsWorkshopPrerequisiteMet() {
  return getProgress().pollinatorGardenRestoration === 'restored';
}

export function isMaterialsWorkshopRouteAvailable() {
  return DISCOVERY_LAB_MATERIALS_SLICE.productionReady
    && isMaterialsWorkshopPrerequisiteMet();
}

export function discoverWashedOutTrail() {
  const progress = getProgress();
  const newlyDiscovered = !progress.washedOutTrailDiscovered;
  if (newlyDiscovered) {
    progress.washedOutTrailDiscovered = true;
    SaveManager.save();
  }
  return { newlyDiscovered, state: getDiscoveryLabState() };
}

export function visitDiscoveryLabDistrict(district: DiscoveryLabDistrict) {
  const world = SaveManager.getWorld(DISCOVERY_LAB_WORLD_ID);
  const firstVisit = world.visited !== true;
  const progress = getProgress();
  world.visited = true;
  world.currentDistrict = district;
  progress.fieldCampusDiscovered = true;
  if (!progress.unlockedDistricts.includes(DISCOVERY_LAB_DISTRICTS.fieldCampus)) {
    progress.unlockedDistricts.push(DISCOVERY_LAB_DISTRICTS.fieldCampus);
  }
  if (!progress.unlockedDistricts.includes(district)) progress.unlockedDistricts.push(district);
  SaveManager.setLastScene('DiscoveryLab');
  SaveManager.save();
  return { firstVisit, state: getDiscoveryLabState() };
}

export function saveDiscoveryLabDistrictProgress(district: DiscoveryLabDistrict, snapshot: unknown) {
  const world = SaveManager.getWorld(DISCOVERY_LAB_WORLD_ID);
  const progress = getProgress();
  progress.districtProgress = { ...(progress.districtProgress ?? {}), [district]: snapshot };
  world.currentDistrict = district;
  if (!progress.unlockedDistricts.includes(district)) progress.unlockedDistricts.push(district);
  SaveManager.save();
  return getDiscoveryLabState();
}

export function completeDiscoveryLabDistrictProgress(
  district: DiscoveryLabDistrict,
  snapshot: unknown,
  restorationEvent: string,
  nextDistrict?: DiscoveryLabDistrict,
) {
  if (!restorationEvent.trim()) throw new RangeError('Discovery Lab restoration event must not be empty.');
  const world = SaveManager.getWorld(DISCOVERY_LAB_WORLD_ID);
  const progress = getProgress();
  progress.districtProgress = { ...(progress.districtProgress ?? {}), [district]: snapshot };
  if (!progress.completedDistricts?.includes(district)) progress.completedDistricts?.push(district);
  if (!progress.restorationEvents?.includes(restorationEvent)) progress.restorationEvents?.push(restorationEvent);
  if (!progress.unlockedDistricts.includes(district)) progress.unlockedDistricts.push(district);
  if (nextDistrict && !progress.unlockedDistricts.includes(nextDistrict)) progress.unlockedDistricts.push(nextDistrict);
  world.currentDistrict = district;
  SaveManager.save();
  return getDiscoveryLabState();
}

export function completeDiscoveryLabInvestigation(
  investigationId: string,
  completionWorldEvent?: 'trail-restoration-1',
) {
  const progress = getProgress();
  progress.washedOutTrailDiscovered = true;
  const newlyCompleted = !progress.investigationsCompleted.includes(investigationId);
  if (newlyCompleted) progress.investigationsCompleted.push(investigationId);
  if (completionWorldEvent === 'trail-restoration-1') {
    progress.washedOutTrailRestoration = 'stage-1';
  }
  SaveManager.save();
  return { newlyCompleted, state: getDiscoveryLabState() };
}

export function unlockDiscoveryLabDistrict(district: DiscoveryLabDistrict) {
  const progress = getProgress();
  const newlyUnlocked = !progress.unlockedDistricts.includes(district);
  if (newlyUnlocked) progress.unlockedDistricts.push(district);
  SaveManager.save();
  return { newlyUnlocked, state: getDiscoveryLabState() };
}

export function unlockPollinatorGardenRoute() {
  if (!DISCOVERY_LAB_POLLINATOR_SLICE.productionReady) {
    return {
      unlocked: false,
      reason: 'production-art-gated' as const,
      state: getDiscoveryLabState(),
    };
  }
  if (getProgress().washedOutTrailRestoration !== 'stage-1') {
    return {
      unlocked: false,
      reason: 'washed-out-trail-required' as const,
      state: getDiscoveryLabState(),
    };
  }
  const result = unlockDiscoveryLabDistrict(DISCOVERY_LAB_DISTRICTS.pollinatorGarden);
  return { unlocked: true, newlyUnlocked: result.newlyUnlocked, state: result.state };
}

export function discoverPollinatorGarden() {
  const route = unlockPollinatorGardenRoute();
  if (!route.unlocked) return { newlyDiscovered: false, ...route };
  const progress = getProgress();
  const newlyDiscovered = !(progress.pollinatorGardenDiscovered ?? false);
  progress.pollinatorGardenDiscovered = true;
  const world = SaveManager.getWorld(DISCOVERY_LAB_WORLD_ID);
  world.currentDistrict = DISCOVERY_LAB_DISTRICTS.pollinatorGarden;
  SaveManager.save();
  return { newlyDiscovered, unlocked: true, state: getDiscoveryLabState() };
}

export function unlockMaterialsWorkshopRoute() {
  if (!DISCOVERY_LAB_MATERIALS_SLICE.productionReady) {
    return {
      unlocked: false,
      reason: 'production-art-gated' as const,
      state: getDiscoveryLabState(),
    };
  }
  if (!isMaterialsWorkshopPrerequisiteMet()) {
    return {
      unlocked: false,
      reason: 'pollinator-garden-required' as const,
      state: getDiscoveryLabState(),
    };
  }
  const result = unlockDiscoveryLabDistrict(DISCOVERY_LAB_DISTRICTS.materialsWorkshop);
  return { unlocked: true, newlyUnlocked: result.newlyUnlocked, state: result.state };
}

export function discoverMaterialsWorkshop() {
  const route = unlockMaterialsWorkshopRoute();
  if (!route.unlocked) return { newlyDiscovered: false, ...route };
  const progress = getProgress();
  const newlyDiscovered = !(progress.materialsWorkshopDiscovered ?? false);
  progress.materialsWorkshopDiscovered = true;
  SaveManager.getWorld(DISCOVERY_LAB_WORLD_ID).currentDistrict =
    DISCOVERY_LAB_DISTRICTS.materialsWorkshop;
  SaveManager.save();
  return { newlyDiscovered, unlocked: true, state: getDiscoveryLabState() };
}

export function persistRelationshipInvestigationSnapshot(
  snapshot: RelationshipInvestigationSnapshot,
) {
  if (
    snapshot.investigationId === DISCOVERY_LAB_POLLINATOR_SLICE.investigation.id
    && !isPollinatorGardenRouteAvailable()
  ) {
    return { persisted: false, reason: 'route-unavailable' as const };
  }
  const progress = getProgress();
  progress.investigationsStarted ??= [];
  progress.relationshipInvestigationSnapshots ??= {};
  if (!progress.investigationsStarted.includes(snapshot.investigationId)) {
    progress.investigationsStarted.push(snapshot.investigationId);
  }
  progress.relationshipInvestigationSnapshots[snapshot.investigationId] = {
    ...snapshot,
    observedInteractionIds: [...snapshot.observedInteractionIds],
    collectedEvidenceIds: [...snapshot.collectedEvidenceIds],
  };
  SaveManager.save();
  return { persisted: true };
}

export function getRelationshipInvestigationSnapshot(investigationId: string) {
  const value = getProgress().relationshipInvestigationSnapshots?.[investigationId];
  if (!value || typeof value !== 'object') return undefined;
  const snapshot = value as Partial<RelationshipInvestigationSnapshot>;
  if (
    snapshot.investigationId !== investigationId
    || typeof snapshot.phase !== 'string'
    || !Array.isArray(snapshot.observedInteractionIds)
    || !Array.isArray(snapshot.collectedEvidenceIds)
  ) return undefined;
  return snapshot as RelationshipInvestigationSnapshot;
}

export function completePollinatorGardenInvestigation(
  snapshot: RelationshipInvestigationSnapshot,
) {
  const config = DISCOVERY_LAB_POLLINATOR_SLICE.investigation;
  const hasRequiredEvidence = config.requiredEvidenceIds.every(id =>
    snapshot.collectedEvidenceIds.includes(id));
  if (
    !isPollinatorGardenRouteAvailable()
    || snapshot.investigationId !== config.id
    || snapshot.phase !== 'complete'
    || !snapshot.comparison
    || !snapshot.comparison.evidenceSupportsCondition
    || snapshot.comparison.supportedConditionId !== config.supportedConditionId
    || !hasRequiredEvidence
  ) {
    return {
      completed: false,
      reason: 'route-unavailable-or-incomplete' as const,
      state: getDiscoveryLabState(),
    };
  }

  const progress = getProgress();
  const newlyCompleted = !progress.investigationsCompleted.includes(config.id);
  if (newlyCompleted) progress.investigationsCompleted.push(config.id);
  progress.investigationsStarted ??= [];
  if (!progress.investigationsStarted.includes(config.id)) {
    progress.investigationsStarted.push(config.id);
  }
  progress.pollinatorGardenDiscovered = true;
  progress.pollinatorGardenRestoration = 'restored';
  progress.evidenceRecords ??= {};
  progress.evidenceRecords[config.id] ??= {
    evidenceIds: [...snapshot.collectedEvidenceIds],
    conclusionId: config.conclusionId,
    completedAt: Date.now(),
  };
  delete progress.relationshipInvestigationSnapshots?.[config.id];
  if (!progress.unlockedDistricts.includes(DISCOVERY_LAB_DISTRICTS.pollinatorGarden)) {
    progress.unlockedDistricts.push(DISCOVERY_LAB_DISTRICTS.pollinatorGarden);
  }
  SaveManager.getWorld(DISCOVERY_LAB_WORLD_ID).currentDistrict =
    DISCOVERY_LAB_DISTRICTS.pollinatorGarden;
  if (newlyCompleted) SaveManager.recordAnswer(config.masteryKey, true);
  SaveManager.save();
  return { completed: true, newlyCompleted, state: getDiscoveryLabState() };
}

export function persistMaterialsInvestigationSnapshot(
  snapshot: MaterialsInvestigationSnapshot,
) {
  if (
    snapshot.investigationId === DISCOVERY_LAB_MATERIALS_SLICE.investigation.id
    && !isMaterialsWorkshopRouteAvailable()
  ) {
    return { persisted: false, reason: 'route-unavailable' as const };
  }
  const progress = getProgress();
  progress.investigationsStarted ??= [];
  progress.materialsInvestigationSnapshots ??= {};
  progress.materialTestRecords ??= {};
  if (!progress.investigationsStarted.includes(snapshot.investigationId)) {
    progress.investigationsStarted.push(snapshot.investigationId);
  }
  progress.materialsInvestigationSnapshots[snapshot.investigationId] = {
    ...snapshot,
    inspectedMaterialIds: [...snapshot.inspectedMaterialIds],
    completedTestKeys: [...snapshot.completedTestKeys],
    observations: snapshot.observations.map(observation => ({ ...observation })),
    evidenceSupportingChoiceIds: [...snapshot.evidenceSupportingChoiceIds],
  };
  const existing = progress.materialTestRecords[snapshot.investigationId];
  progress.materialTestRecords[snapshot.investigationId] = {
    completedTestKeys: [...snapshot.completedTestKeys],
    evidenceIds: snapshot.observations.map(observation => observation.evidenceId),
    selectedMaterialId: snapshot.selectedMaterialId,
    completedAt: existing?.completedAt,
  };
  SaveManager.save();
  return { persisted: true };
}

export function getMaterialsInvestigationSnapshot(investigationId: string) {
  const value = getProgress().materialsInvestigationSnapshots?.[investigationId];
  if (!value || typeof value !== 'object') return undefined;
  const snapshot = value as Partial<MaterialsInvestigationSnapshot>;
  if (
    snapshot.investigationId !== investigationId
    || typeof snapshot.phase !== 'string'
    || !Array.isArray(snapshot.inspectedMaterialIds)
    || !Array.isArray(snapshot.completedTestKeys)
    || !Array.isArray(snapshot.observations)
    || !Array.isArray(snapshot.evidenceSupportingChoiceIds)
  ) return undefined;
  return snapshot as MaterialsInvestigationSnapshot;
}

export function completeMaterialsWorkshopInvestigation(
  snapshot: MaterialsInvestigationSnapshot,
) {
  const config = DISCOVERY_LAB_MATERIALS_SLICE.investigation;
  const hasRequiredEvidence = config.task.requiredObservationKeys.every(key =>
    snapshot.completedTestKeys.includes(key));
  if (
    !isMaterialsWorkshopRouteAvailable()
    || snapshot.investigationId !== config.id
    || snapshot.phase !== 'complete'
    || snapshot.selectedMaterialId !== config.bestMaterialId
    || !snapshot.choice?.suitedToPurpose
    || !hasRequiredEvidence
  ) {
    return {
      completed: false,
      reason: 'route-unavailable-or-incomplete' as const,
      state: getDiscoveryLabState(),
    };
  }

  const progress = getProgress();
  const newlyCompleted = !progress.investigationsCompleted.includes(config.id);
  if (newlyCompleted) progress.investigationsCompleted.push(config.id);
  progress.investigationsStarted ??= [];
  if (!progress.investigationsStarted.includes(config.id)) {
    progress.investigationsStarted.push(config.id);
  }
  progress.materialsWorkshopDiscovered = true;
  progress.materialsWorkshopRestoration = 'restored';
  progress.evidenceRecords ??= {};
  progress.evidenceRecords[config.id] ??= {
    evidenceIds: [...snapshot.evidenceSupportingChoiceIds],
    conclusionId: config.conclusionId,
    completedAt: Date.now(),
  };
  progress.materialTestRecords ??= {};
  const existingTestRecord = progress.materialTestRecords[config.id];
  progress.materialTestRecords[config.id] = {
    completedTestKeys: [...snapshot.completedTestKeys],
    evidenceIds: snapshot.observations.map(observation => observation.evidenceId),
    selectedMaterialId: snapshot.selectedMaterialId,
    completedAt: existingTestRecord?.completedAt ?? Date.now(),
  };
  delete progress.materialsInvestigationSnapshots?.[config.id];
  if (!progress.unlockedDistricts.includes(DISCOVERY_LAB_DISTRICTS.materialsWorkshop)) {
    progress.unlockedDistricts.push(DISCOVERY_LAB_DISTRICTS.materialsWorkshop);
  }
  SaveManager.getWorld(DISCOVERY_LAB_WORLD_ID).currentDistrict =
    DISCOVERY_LAB_DISTRICTS.materialsWorkshop;
  if (newlyCompleted) SaveManager.recordAnswer(config.masteryKey, true);
  SaveManager.save();
  return { completed: true, newlyCompleted, state: getDiscoveryLabState() };
}
